import { arrayToObject, EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import { getInsuranceItems, isCheckoutPostPurchaseInsurance } from '../insurance'
import getCheckoutSelectedQuotes from '../getCheckoutSelectedQuote'
import { getOrderInsuranceUpdateQuoteKey } from 'lib/order/orderInsuranceUtils'
import getTotalsWithoutInsurance from '../../payment/getTotalsWithoutInsurance'
import getLoyaltyInsuranceProductType from 'loyalty/lib/getLoyaltyInsuranceProductType'
import { checkoutWithMemberPrice, selectShouldUseInsuranceMemberPrice } from '../luxPlusSubscription'
import getCanRedeemInsuranceLoyaltyBenefit from 'checkout/selectors/loyalty/getCanRedeemInsuranceLoyaltyBenefit'
import { capitalise } from 'lib/string/stringUtils'
import { PRODUCT_ID_PASSPORT_CARD, INSURANCE_TYPE_PASSPORT_CARD } from 'constants/insurance'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'
import {
  getPassportCardQuoteRequestKey,
  getPassportCardSelectedOptionalBenefitCodes,
} from 'lib/insurance/passportCardQuoteUtils'
import getPassportCardCheckoutQuoteRequestParams from 'checkout/selectors/request/getPassportCardCheckoutQuoteRequestParams'
import { getPassportCardCheckoutCountries } from 'checkout/selectors/request/insurance'
import getPassportCardQuoteItems from './getPassportCardQuoteItems'
import {
  getPassportCardUpdateCoverageAmount,
  toPassportCardTravellersDetailsV2,
} from 'checkout/lib/insurance/passportCardUpdateSnapshotUtils'
import { getTotalPriceFromExistingOrder } from 'checkout/selectors/request/getCheckoutInsuranceCoverAmount'

function buildPassportCardUpdateSnapshot(
  item: App.Checkout.InsuranceItem,
  destinationCountries: Array<string>,
  quoteItems: Array<App.PassportCardQuoteItem>,
): App.PassportCardUpdateSnapshot {
  return {
    destinationCountries: item.countries ?? destinationCountries,
    items: quoteItems,
    travellersDetailsV2: toPassportCardTravellersDetailsV2(item.passportCardTravellers),
    optionalCovers: item.passportCardOptionalCovers,
  }
}

function getPassportCardSelectedOptionalBenefitCodeSet(
  item: App.Checkout.InsuranceItem,
  proposal: App.PassportCardProposal,
): Set<string> {
  if (item.passportCardOptionalBenefitCodes !== undefined) {
    return new Set(item.passportCardOptionalBenefitCodes)
  }

  return new Set(getPassportCardSelectedOptionalBenefitCodes(proposal.optionalBenefits))
}

function getPassportCardTotalPrice(
  item: App.Checkout.InsuranceItem,
  proposal: App.PassportCardProposal,
): number {
  const selectedOptionalBenefitCodes = getPassportCardSelectedOptionalBenefitCodeSet(item, proposal)

  return proposal.optionalBenefits.reduce((total, benefit) => {
    if (selectedOptionalBenefitCodes.has(benefit.code)) {
      return total + benefit.price.consumerPrice
    }
    return total
  }, proposal.price.consumerPrice)
}

function getSelectedPassportCardQuoteRequestParams(state: App.State) {
  const passportCardInsuranceItem = getInsuranceItems(state)
    .find(item => item.productId === PRODUCT_ID_PASSPORT_CARD)

  if (!passportCardInsuranceItem) return undefined

  return getPassportCardCheckoutQuoteRequestParams(state, {
    insuranceItem: passportCardInsuranceItem,
  })
}

/**
 * Retrieves a record of item id -> update quote for all post-purchase
 * insurance items currently in the checkout
 */
const getCheckoutSelectedQuoteUpdates = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => getTotalsWithoutInsurance(state).data.price,
  (state: App.State) => state.insurance.updateQuotes,
  (orderItems, items, price, updateQuotes): Record<string, App.InsuranceUpdateQuote | undefined> => {
    return arrayToObject(items,
      item => item.itemId,
      item => {
        const orderItem = orderItems?.find(orderItem => orderItem.id === item.orderItemId)
        if (orderItem) {
          const key = getOrderInsuranceUpdateQuoteKey({
            itemId: orderItem.id,
            startDate: item.startDate ?? orderItem.startDate,
            endDate: item.endDate ?? orderItem.endDate,
            travellers: item.travellers ?? orderItem.travellers,
            coverageAmount: item.coverageAmount ?? orderItem.coverAmount + price,
          })

          return updateQuotes[key]?.quote
        }
      })
  })

/**
 * Retrieves a record of item id -> PassportCard update quote for all
 * post-purchase PassportCard insurance items currently in the checkout
 */
const getCheckoutSelectedPassportCardQuoteUpdates = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => getTotalsWithoutInsurance(state).data.price,
  (state: App.State) => getTotalPriceFromExistingOrder(state),
  (state: App.State) => state.passportCardInsurance.updateQuotes,
  (state: App.State) => getPassportCardCheckoutCountries(state),
  (state: App.State) => getPassportCardQuoteItems(state),
  (orderItems, items, price, existingOrderCoverageAmount, passportCardUpdateQuotes, destinationCountries, quoteItems): Record<string, App.PassportCardInsuranceUpdateQuote | undefined> => {
    return arrayToObject(items,
      item => item.itemId,
      item => {
        const orderItem = orderItems?.find(orderItem => orderItem.id === item.orderItemId)
        if (orderItem?.insuranceType === INSURANCE_TYPE_PASSPORT_CARD) {
          const passportCardUpdateSnapshot = buildPassportCardUpdateSnapshot(item, destinationCountries, quoteItems)
          const key = getOrderInsuranceUpdateQuoteKey({
            itemId: orderItem.id,
            startDate: item.startDate ?? orderItem.startDate,
            endDate: item.endDate ?? orderItem.endDate,
            travellers: item.travellers ?? orderItem.travellers,
            coverageAmount: item.coverageAmount ?? getPassportCardUpdateCoverageAmount({
              existingOrderCoverageAmount,
              orderItemCoverAmount: orderItem.coverAmount,
              priceDiff: price,
            }),
            passportCardUpdateSnapshot,
          })

          return passportCardUpdateQuotes[key]?.quote
        }
      })
  })

const getInsuranceItemsView = createSelector(
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => state.insurance.products,
  (state: App.State) => getCheckoutSelectedQuotes(state),
  (state: App.State) => getCheckoutSelectedQuoteUpdates(state),
  (state: App.State) => getCheckoutSelectedPassportCardQuoteUpdates(state),
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (state: App.State) => selectShouldUseInsuranceMemberPrice(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getCanRedeemInsuranceLoyaltyBenefit(state),
  (state: App.State) => state.geo.insuranceProductName,
  (state: App.State) => isCheckoutPostPurchaseInsurance(state),
  (state: App.State) => state.passportCardInsurance.quotes,
  (state: App.State) => getSelectedPassportCardQuoteRequestParams(state),
  (
    insuranceItems,
    products,
    quotes,
    updateQuotes,
    passportCardUpdateQuotes,
    orderInsuranceItems = EmptyArray,
    selectShouldUseInsuranceMemberPrice,
    checkoutWithMemberPrice,
    canRedeemInsuranceLoyaltyBenefit,
    insuranceName,
    containsFlighsWithEmbeddedInsurance,
    passportCardQuotes,
    passportCardQuoteRequestParams,
  ): App.WithDataStatus<Array<App.Checkout.InsuranceItemView>> => {
    const intl = getCurrentIntl()
    let hasRequiredData = true
    const itemViews = insuranceItems.map((item): App.Checkout.InsuranceItemView | null => {
      let title = intl.formatMessage(
        {
          id: 'gEQvw_-fXFM_tN6KeX7_',
          defaultMessage: 'Travel {insuranceName}',
          description: 'Heading for the travel insurance product section in the checkout flow',
        },
        { insuranceName: capitalise(insuranceName) },
      )
      if (item.insuranceType === 'cfmr') {
        title = containsFlighsWithEmbeddedInsurance ? intl.formatMessage(
          bookingProtectionGlossaryMessages.luxuryEscapesProtect,
        ) : intl.formatMessage(
          {
            id: 'NCsrRo4PUPacWPdfisea',
            defaultMessage: 'Cancellation {insuranceName}',
            description: 'Heading for the cancellation insurance product section in the checkout flow',
          },
          { insuranceName: capitalise(insuranceName) },
        )
      }
      if (item.productId) {
        // PassportCard uses a different quotes and products logic
        if (item.productId === PRODUCT_ID_PASSPORT_CARD) {
          const title = intl.formatMessage(
            {
              id: 'f9xwQdzJwGxwvbCI48Gr',
              defaultMessage: 'Travel Insurance',
              description: 'Title for PassportCard travel insurance in checkout insurance item view',
            },
          )

          if (!passportCardQuoteRequestParams) {
            hasRequiredData = false
            return null
          }

          const requestKey = getPassportCardQuoteRequestKey(passportCardQuoteRequestParams)
          const passportCardQuote = passportCardQuotes[requestKey]
          const proposal = passportCardQuote?.proposals[0]
          // Identity comes from the resolved quote, never from the ids stamped on the item
          // when protection was added. Editing the coverage window re-keys the request, so
          // the resolved quote is the one the customer has been shown and priced while the
          // item still carries the pre-edit ids; reading the item there buys the superseded
          // proposal while displaying the new window (CRO-7086). There is deliberately no
          // fallback to the item: the guard below already drops this item when no quote has
          // resolved, so an item id could only ever shadow a live one.
          const quoteId = passportCardQuote?.quoteId

          if (!proposal || !quoteId) {
            hasRequiredData = false
            return null
          }

          const baseLoyaltyPrice = proposal.loyaltyPrice ?? proposal.price.consumerPrice
          const optionalBenefitsTotal = getPassportCardTotalPrice(item, proposal) - proposal.price.consumerPrice
          const loyaltyTotal = baseLoyaltyPrice + optionalBenefitsTotal
          const publicTotal = getPassportCardTotalPrice(item, proposal)

          return {
            title,
            item,
            quoteId,
            proposalId: proposal.proposalId,
            productId: item.productId,
            name: proposal.communications.title || title,
            policyNames: [],
            startDate: passportCardQuoteRequestParams.tripStartDate,
            endDate: passportCardQuoteRequestParams.tripEndDate,
            destinationCountries: passportCardQuoteRequestParams.destinationCountries,
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              // No selectShouldUseInsuranceMemberPrice guard here (unlike CoverGenius) because
              // PassportCard does not support LuxPlus+ member pricing - loyalty is the only discount type.
              savingsAmount: canRedeemInsuranceLoyaltyBenefit ? publicTotal - loyaltyTotal : 0,
            },
            totals: {
              price: canRedeemInsuranceLoyaltyBenefit ? loyaltyTotal : publicTotal,
              memberPrice: 0,
              value: publicTotal,
              surcharge: 0,
              taxesAndFees: 0,
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            benefits: proposal.communications.covers,
            mobileAppPrice: 0,
            coverageAmount: item.coverageAmount ?? 0,
          }
        }

        const product = products[item.productId]
        const quote = quotes[item.itemId]
        hasRequiredData = !!(product && quote)
        if (product && quote) {
          const selectedPolicyIds = new Set(item.policyIds)
          const selectedPolicyNames = product.policyOptions
            .filter(option => selectedPolicyIds.has(option.policy))
            .map(option => option.name)
          const policyNames = product.policyNames.length ? product.policyNames : selectedPolicyNames
          let benefits: Array<string> = []
          const isCustomizedProtectionSelected = selectedPolicyNames.length > 0
          if (isCustomizedProtectionSelected) {
            benefits = item.policyIds?.flatMap((selectedPolicyId) => {
              return product.policyOptions?.find((p) => p.policy === selectedPolicyId)?.keyBenefits?.map(benefit => benefit.label) ?? []
            }) ?? []
          } else {
            benefits = product.keyBenefits.map((benefit) => benefit.label)
          }

          const total = canRedeemInsuranceLoyaltyBenefit ? quote.loyaltyTotal : quote.total
          const useMobilePrice = quote.mobileAppOnlyPrice && !canRedeemInsuranceLoyaltyBenefit

          return {
            title,
            item,
            quoteId: quote.id,
            productId: item.productId,
            name: product.name,
            policyNames,
            startDate: quote.startDate,
            endDate: quote.endDate,
            destinationCountries: quote.destinationCountries,
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              savingsAmount: canRedeemInsuranceLoyaltyBenefit && !selectShouldUseInsuranceMemberPrice ? quote.total - quote.loyaltyTotal : 0,
            },
            totals: {
              price: useMobilePrice ? quote.mobileAppOnlyPrice : total,
              // Flight CFMR's member price (luxPlusFlightTotal) is gated server-side by
              // svc-insurance's own kill switch and computed independently of the legacy
              // insurance-discount flag/eligibility (selectShouldUseInsuranceMemberPrice) - so it
              // must key off field presence + real membership (checkoutWithMemberPrice), not that
              // flag, or this silently falls back to the full premium whenever the flag is off.
              memberPrice: quote.luxPlusFlightTotal !== undefined
                ? (checkoutWithMemberPrice ? quote.luxPlusFlightTotal || quote.total : 0)
                : (selectShouldUseInsuranceMemberPrice ? quote.luxPlusTotal || quote.total : 0),
              value: quote.total,
              surcharge: 0,
              taxesAndFees: 0,
              ...(useMobilePrice && quote.mobileAppOnlyPrice > 0 && {
                mobileAppDiscount: {
                  amount: quote.total - quote.mobileAppOnlyPrice,
                  percentage: 20,
                },
              }),
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            benefits,
            mobileAppPrice: useMobilePrice ? quote.mobileAppOnlyPrice ?? 0 : 0,
            coverageAmount: quote.coverageAmount,
          }
        }
      } else if (item.orderItemId) {
        const orderItem = orderInsuranceItems.find(orderItem => orderItem.id === item.orderItemId)!
        const passportCardQuote = passportCardUpdateQuotes[item.itemId]

        if (passportCardQuote) {
          hasRequiredData = true
          return {
            title,
            item,
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              savingsAmount: 0,
            },
            updateQuoteId: passportCardQuote.proposalId,
            productId: orderItem.idProduct!,
            name: orderItem.productName,
            policyNames: orderItem.policyNames ?? [],
            startDate: passportCardQuote.startDate,
            endDate: passportCardQuote.endDate,
            destinationCountries: orderItem.destinations ?? [],
            totals: {
              price: passportCardQuote.priceDiff.amount,
              memberPrice: passportCardQuote.priceDiff.amount,
              value: passportCardQuote.newPrice.amount,
              paidPrice: 0,
              newPrice: passportCardQuote.priceDiff.amount,
              surcharge: 0,
              taxesAndFees: 0,
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            coverageAmount: passportCardQuote.coverageAmount,
            benefits: [],
            mobileAppPrice: 0,
          }
        }

        const updateQuote = updateQuotes[item.itemId]
        hasRequiredData = !!updateQuote
        if (updateQuote) {
          return {
            title,
            item,
            loyalty: {
              productType: getLoyaltyInsuranceProductType(item.insuranceType),
              savingsAmount: 0,
            },
            updateQuoteId: updateQuote.id,
            productId: orderItem.idProduct!,
            name: orderItem.productName,
            policyNames: orderItem.policyNames ?? [],
            startDate: updateQuote.startDate,
            endDate: updateQuote.endDate,
            destinationCountries: orderItem.destinations ?? [],
            totals: {
              price: updateQuote.totalPriceDiff,
              // note: member price not implemented on update quotes yet?
              memberPrice: updateQuote.totalPriceDiff,
              value: updateQuote.total,
              surcharge: 0,
              taxesAndFees: 0,
              memberValue: 0,
              extraGuestSurcharge: 0,
            },
            coverageAmount: updateQuote.coverageAmount,
            benefits: [],
            mobileAppPrice: 0,
          }
        }
      }

      return null
    })

    return {
      hasRequiredData,
      data: nonNullable(itemViews),
    }
  },
)

export default getInsuranceItemsView
